import React from 'react';
import { MapPin, Plus, Sparkles } from 'lucide-react';
import { Area, Locality } from '../../../types';

interface AddressSectionProps {
  formData: {
    building: string;
    streetName: string;
    landmark: string;
    subLocality: string;
    areaId: string;
    areaName: string;
    localityId: string;
    localityName: string;
    latitude: number;
    longitude: number;
  };
  areas: Area[];
  localities: Locality[];
  editMode?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  onFocus: (fieldName: string) => void;
  onBlur: (fieldName: string) => void;
  getPlaceholder: (fieldName: string, defaultPlaceholder: string) => string;
  onAutoFill?: () => void;
  autoFillLoading?: boolean;
}

export const AddressSection: React.FC<AddressSectionProps> = ({
  formData,
  areas,
  localities,
  editMode = false,
  onChange,
  onFocus,
  onBlur,
  getPlaceholder,
  onAutoFill,
  autoFillLoading = false
}) => {
  return (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center space-x-2">
        <MapPin className="w-5 h-5 text-green-600" />
        <span>Address</span>
      </h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Building/Shop No.</label>
          <input
            type="text"
            name="building"
            value={formData.building}
            onChange={onChange}
            onFocus={() => onFocus('building')}
            onBlur={() => onBlur('building')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={getPlaceholder('building', 'Building or shop number')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Street Name</label>
          <input
            type="text"
            name="streetName"
            value={formData.streetName}
            onChange={onChange}
            onFocus={() => onFocus('streetName')}
            onBlur={() => onBlur('streetName')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={getPlaceholder('streetName', 'Street name')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Landmark</label>
          <input
            type="text"  
            name="landmark"
            value={formData.landmark}
            onChange={onChange}
            onFocus={() => onFocus('landmark')}
            onBlur={() => onBlur('landmark')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={getPlaceholder('landmark', 'Nearby landmark')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Sub-Locality</label>
          <input
            type="text"
            name="subLocality"
            value={formData.subLocality}
            onChange={onChange}
            onFocus={() => onFocus('subLocality')}
            onBlur={() => onBlur('subLocality')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={getPlaceholder('subLocality', 'Sub-locality')}
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Area (Optional)</label>
          <select
            name="areaId"
            value={formData.areaId}
            onChange={onChange}
            disabled={areas.length === 0}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
          >
            <option value="">Select Area</option>
            {areas.map(area => (
              <option key={area.id} value={area.id}>{area.name}</option>
            ))}
          </select>
          
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Locality (Optional)</label>
          <select
            name="localityId"
            value={formData.localityId}
            onChange={onChange}
            disabled={!formData.areaId || localities.length === 0}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
          >
            <option value="">Select Locality</option>
            {localities.map(locality => (
              <option key={locality.id} value={locality.id}>{locality.name}</option>
            ))}
          </select>
          
        </div>
      </div>
    </div>
  );
};

export default AddressSection;
